import React from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { LANGUAGES } from '../../context/LanguageContext';

/**
 * Language Toggle Component
 *
 * Provides a dropdown for switching between Tamil, Sinhala, and English
 * Features:
 * - Clean, accessible dropdown design
 * - Automatic language detection
 * - Visual indication of current language
 * - Mobile responsive
 * - RTL support for future languages
 *
 * @param {Object} props
 * @param {string} props.className - Additional CSS classes
 * @param {string} props.variant - Display variant ('dropdown', 'tabs', 'compact')
 */
const LanguageToggle = ({
  className = '',
  variant = 'dropdown'
}) => {
  const {
    currentLanguage,
    availableLanguages,
    changeLanguage,
    t
  } = useLanguage();

  const handleLanguageChange = (languageCode) => {
    changeLanguage(languageCode);
  };

  // Compact variant (for mobile or minimal UI)
  if (variant === 'compact') {
    return (
      <div className={`language-toggle-compact ${className}`}>
        <select
          value={currentLanguage}
          onChange={(e) => handleLanguageChange(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
          aria-label={t('language')}
          title={t('language')}
        >
          {availableLanguages.map((lang) => (
            <option key={lang.code} value={lang.code}>
              {lang.native_name || lang.nativeName || lang.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Tabs variant (for header navigation)
  if (variant === 'tabs') {
    return (
      <div className={`language-toggle-tabs ${className}`}>
        <div className="flex space-x-1" role="tablist">
          {availableLanguages.map((lang) => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`
                px-4 py-2 rounded-lg font-medium text-sm transition-all duration-200
                ${currentLanguage === lang.code
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }
              `}
              role="tab"
              aria-selected={currentLanguage === lang.code}
              aria-controls={`${lang.code}-panel`}
              aria-label={`Switch to ${lang.name || lang.native_name || lang.nativeName} language`}
              title={`Switch to ${lang.name || lang.native_name || lang.nativeName} language`}
              lang={lang.code}
            >
              {lang.native_name || lang.nativeName || lang.name}
            </button>
          ))}
        </div>
      </div>
    );
  }

  // Default dropdown variant
  return (
    <div className={`language-toggle ${className}`}>
      <div className="relative group">
        <button
          className="
            flex items-center space-x-2 px-4 py-2
            bg-white border border-gray-300 rounded-lg
            hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500
            transition-all duration-200
            shadow-sm hover:shadow-md
          "
          aria-label={t('language')}
          title={t('language')}
          aria-expanded="false"
        >
          <span className="font-medium text-gray-700">
            {LANGUAGES[currentLanguage]?.nativeName || LANGUAGES[currentLanguage]?.native_name || LANGUAGES[currentLanguage]?.name}
          </span>
          <svg
            className="w-4 h-4 text-gray-500"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </button>

        {/* Dropdown Menu */}
        <div className="
          absolute top-full right-0 mt-2 w-48
          bg-white border border-gray-200 rounded-lg
          shadow-lg opacity-0 invisible
          group-hover:opacity-100 group-hover:visible
          transition-all duration-200 z-50
        ">
          <div className="py-2">
            {availableLanguages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => handleLanguageChange(lang.code)}
                className={`
                  w-full text-left px-4 py-2 text-sm
                  hover:bg-gray-50 transition-colors duration-150
                  flex items-center space-x-3
                  ${currentLanguage === lang.code ? 'bg-blue-50 text-blue-600' : 'text-gray-700'}
                `}
                role="option"
                aria-selected={currentLanguage === lang.code}
                aria-label={`Switch to ${lang.name || lang.native_name || lang.nativeName} language`}
                title={`Switch to ${lang.name || lang.native_name || lang.nativeName} language`}
                lang={lang.code}
              >
                <span className="font-medium">
                  {lang.native_name || lang.nativeName || lang.name}
                </span>
                {currentLanguage === lang.code && (
                  <svg
                    className="w-4 h-4 text-blue-600"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                  >
                    <path
                      fillRule="evenodd"
                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LanguageToggle;